/**
 * The left sidebar is a stack of widgets. The Library sits at the bottom and keeps whatever
 * room is left; every widget listed here can be shown or hidden on its own and stacks above
 * it in this order. A widget on the right side opens in the right sidebar instead, above the
 * Time Machine, wherever the screen is wide enough to have one. Adding one takes four
 * things: an entry here, a panel in index.html carrying the ids these helpers spell, a View
 * menu item, and a preference key in preferences.ts. The sidebar test fails until all four
 * are there.
 */
export const SIDEBAR_WIDGETS = [
  { id: "outline", label: "Outline", focus: "outline-list", side: "right" },
  { id: "calendar", label: "Calendar", focus: "calendar-grid", side: "left" },
  { id: "timer", label: "Timer", focus: "timer-run", side: "left" },
  { id: "calculator", label: "Calculator", focus: "calculator-input", side: "left" },
] as const;
export type SidebarWidget = (typeof SIDEBAR_WIDGETS)[number];
export type SidebarWidgetId = SidebarWidget["id"];
export const sidebarWidget = (id: SidebarWidgetId): SidebarWidget =>
  SIDEBAR_WIDGETS.find((widget) => widget.id === id)!;
export const widgetPanelId = (id: SidebarWidgetId) => `${id}-widget` as const;
export const widgetToggleId = (id: SidebarWidgetId) => `${id}-toggle` as const;
export const widgetCloseId = (id: SidebarWidgetId) => `${id}-close` as const;
export const widgetHandleId = (id: SidebarWidgetId) => `${id}-handle` as const;
export const widgetBodyId = (id: SidebarWidgetId) => `${id}-body` as const;
/** Kept apart from the account: which widgets are open is about this screen, not the vault. */
export const widgetPreferenceKey = (id: SidebarWidgetId) => `sidebar-${id}` as const;
/** The ids a stored list names, without the unknown ones and without repeats. */
export const widgetList = (stored: string | null): SidebarWidgetId[] => [
  ...new Set(
    (stored ?? "")
      .split(",")
      .filter((id): id is SidebarWidgetId =>
        SIDEBAR_WIDGETS.some((widget) => widget.id === id),
      ),
  ),
];
/**
 * The order to stack the widgets in. A widget this browser has never heard of goes to the
 * bottom of the stack rather than nowhere, so a new one shows up without clearing anybody's
 * arrangement.
 */
export const widgetOrderFrom = (stored: string | null): SidebarWidgetId[] => {
  const named = widgetList(stored);
  return [
    ...named,
    ...SIDEBAR_WIDGETS.map((widget) => widget.id).filter((id) => !named.includes(id)),
  ];
};
/** Lifts one widget out and sets it down above `before`, or at the bottom for null. */
export function moveWidgetBefore(
  order: readonly SidebarWidgetId[],
  id: SidebarWidgetId,
  before: SidebarWidgetId | null,
): SidebarWidgetId[] {
  if (before === id) return [...order];
  const rest = order.filter((other) => other !== id);
  const at = before === null ? -1 : rest.indexOf(before);
  return at < 0 ? [...rest, id] : [...rest.slice(0, at), id, ...rest.slice(at)];
}
export type WidgetBox = { id: SidebarWidgetId; top: number; bottom: number };
/**
 * The widget a dragged one should settle above, given where the pointer is and the boxes
 * on screen in stacking order. Crossing the middle of a box is what moves it past it, so a
 * widget does not swap back and forth while the pointer sits on a boundary.
 */
export function widgetDropTarget(
  boxes: readonly WidgetBox[],
  dragged: SidebarWidgetId,
  y: number,
): SidebarWidgetId | null {
  for (const box of boxes) {
    if (box.id === dragged) continue;
    if (y < (box.top + box.bottom) / 2) return box.id;
  }
  return null;
}
